import { ArrowBackIcon, ViewIcon } from "@chakra-ui/icons"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import React from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import Select from "@/components/Common/AppSelect"
import {
  type ApiError,
  LoginService,
  type Role,
  type UserPublic,
  UsersService,
  type UserUpdate,
} from "../../../client"
import useCustomToast from "../../../hooks/useCustomToast"
import { emailPattern, handleError } from "../../../utils"

export const Route = createFileRoute("/_layout/admin/users/$userId")({
  component: AdminUserProfilePage,
})

interface UserUpdateForm extends UserUpdate {
  roleCode: string
  roleLevel: string
}

function AdminUserProfilePage() {
  const { userId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const cardBg = useColorModeValue("white", "gray.800")
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.600", "gray.400")

  const { data: user, isPending } = useQuery({
    queryKey: ["users", "detail", userId],
    queryFn: () => UsersService.readUserById({ userId }),
  })

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: () => UsersService.readRoles(),
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UserUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: "",
      fullName: "",
      isActive: true,
      acronym: "",
      roleCode: "",
      roleLevel: "",
    },
  })

  React.useEffect(() => {
    if (!user) return
    reset({
      email: user.email,
      fullName: user.fullName ?? "",
      isActive: user.isActive ?? true,
      acronym: user.acronym ?? "",
      roleCode:
        Array.isArray(user.roles) && user.roles.length > 0
          ? user.roles[0].code
          : "",
      roleLevel:
        Array.isArray(user.roles) && user.roles.length > 0
          ? (user.roles[0].level || "").toLowerCase()
          : "",
    })
  }, [user, reset])

  const rolesByLevel = React.useMemo(() => {
    const grouped: Record<string, Role[]> = {}
    roles.forEach((role) => {
      const level = (role.level || "").toLowerCase()
      if (!grouped[level]) grouped[level] = []
      grouped[level].push(role)
    })
    return grouped
  }, [roles])

  const availableLevels = React.useMemo(
    () => Object.keys(rolesByLevel),
    [rolesByLevel],
  )
  const selectedLevel = watch("roleLevel")

  React.useEffect(() => {
    if (!selectedLevel && availableLevels.length > 0) {
      setValue("roleLevel", availableLevels[0], { shouldValidate: true })
    }
  }, [availableLevels, selectedLevel, setValue])

  const updateMutation = useMutation({
    mutationFn: (data: UserUpdateForm) =>
      UsersService.updateUser({
        userId,
        requestBody: {
          email: data.email,
          fullName: data.fullName,
          isActive: data.isActive,
          acronym: data.acronym,
          roles: data.roleCode ? [data.roleCode.trim().toUpperCase()] : [],
        },
      }),
    onSuccess: () => {
      showToast("Sukces!", "Użytkownik został zaktualizowany.", "success")
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["users", "detail", userId] })
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () => UsersService.deleteUser({ userId }),
    onSuccess: async () => {
      showToast("Sukces!", "Użytkownik został zarchiwizowany.", "success")
      await queryClient.invalidateQueries({ queryKey: ["users"] })
      await queryClient.invalidateQueries({
        queryKey: ["users", "detail", userId],
      })
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: () => UsersService.restoreUser({ userId }),
    onSuccess: async () => {
      showToast("Sukces!", "Użytkownik został przywrócony.", "success")
      await queryClient.invalidateQueries({ queryKey: ["users"] })
      await queryClient.invalidateQueries({
        queryKey: ["users", "detail", userId],
      })
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) =>
      LoginService.recoverPassword({
        requestBody: { email },
      }),
    onSuccess: () => {
      showToast(
        "Sukces!",
        "Wiadomość resetująca hasło została wysłana.",
        "success",
      )
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<UserUpdateForm> = (data) => {
    updateMutation.mutate(data)
  }

  if (isPending || !user) {
    return (
      <Container maxW="5xl" py={6}>
        <Flex minH="240px" align="center" justify="center" gap={3}>
          <Spinner />
          <Text>Ładowanie profilu użytkownika...</Text>
        </Flex>
      </Container>
    )
  }

  const isArchived = !!user.deletedAt
  const isCurrentUser = currentUser?.ID === user.ID

  return (
    <Container maxW="5xl" py={6}>
      <Stack spacing={5}>
        <HStack justify="space-between" align="center" flexWrap="wrap">
          <HStack spacing={3}>
            <Button
              size="sm"
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              onClick={() =>
                navigate({
                  to: "/admin",
                  search: {
                    page: 1,
                    pageSize: 10,
                  },
                })
              }
            >
              Powrót do użytkowników
            </Button>
            <Box>
              <Heading size="lg">{user.fullName || user.email}</Heading>
              <HStack mt={1} spacing={2}>
                <Badge colorScheme={isArchived ? "orange" : "green"}>
                  {isArchived ? "Zarchiwizowany" : "Aktywny"}
                </Badge>
                {isCurrentUser && <Badge colorScheme="teal">Ty</Badge>}
              </HStack>
            </Box>
          </HStack>
          <HStack>
            {isArchived ? (
              <Button
                size="sm"
                colorScheme="green"
                onClick={() => restoreMutation.mutate()}
                isLoading={restoreMutation.isPending}
              >
                Przywróć użytkownika
              </Button>
            ) : (
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => archiveMutation.mutate()}
                isLoading={archiveMutation.isPending}
                isDisabled={isCurrentUser}
              >
                Zarchiwizuj użytkownika
              </Button>
            )}
          </HStack>
        </HStack>

        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={cardBorder}
          borderRadius="2xl"
          p={{ base: 4, md: 6 }}
          boxShadow="sm"
        >
          <Stack spacing={6} as="form" onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <Heading size="md">Profil użytkownika</Heading>
              <Text mt={1} color={mutedText}>
                Zarządzaj danymi tożsamości, przypisaniem roli i statusem konta
                z jednego miejsca.
              </Text>
            </Box>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                size="sm"
                id="email"
                {...register("email", {
                  required: "Email jest wymagany",
                  pattern: emailPattern,
                })}
                type="email"
              />
              {errors.email && (
                <FormErrorMessage>{errors.email.message}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="name">Imię i nazwisko</FormLabel>
              <Input
                size="sm"
                id="name"
                {...register("fullName")}
                type="text"
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="acronym">Akronim</FormLabel>
              <Input
                size="sm"
                id="acronym"
                maxLength={5}
                {...register("acronym")}
                type="text"
              />
            </FormControl>

            <FormControl isInvalid={!!errors.roleCode}>
              <FormLabel htmlFor="roleLevel">Poziom</FormLabel>
              {rolesLoading ? (
                <Flex align="center" gap={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Ładowanie poziomów...</Text>
                </Flex>
              ) : (
                <Select
                  size="sm"
                  id="roleLevel"
                  value={selectedLevel}
                  onChange={(e) =>
                    setValue("roleLevel", e.target.value, {
                      shouldValidate: true,
                    })
                  }
                >
                  {availableLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </Select>
              )}
              <FormHelperText>
                Najpierw wybierz poziom (user/admin/superadmin).
              </FormHelperText>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.roleCode}>
              <FormLabel htmlFor="roleCode">Rola</FormLabel>
              {rolesLoading ? (
                <Flex align="center" gap={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Ładowanie ról...</Text>
                </Flex>
              ) : (
                <Controller
                  control={control}
                  name="roleCode"
                  rules={{
                    required: "Rola jest wymagana",
                  }}
                  render={({ field }) => {
                    const levelRoles = selectedLevel
                      ? rolesByLevel[selectedLevel] || []
                      : []
                    return (
                      <Stack spacing={2}>
                        {levelRoles.map((role) => (
                          <Box
                            key={role.code}
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                            cursor="pointer"
                            bg={
                              field.value === role.code
                                ? "main.accent"
                                : "transparent"
                            }
                            borderColor={
                              field.value === role.code
                                ? "main.accent"
                                : cardBorder
                            }
                            color={
                              field.value === role.code ? "white" : "inherit"
                            }
                            onClick={() => field.onChange(role.code)}
                            _hover={{ borderColor: "main.accent" }}
                          >
                            <Flex align="center" justify="space-between">
                              <Box>
                                <Text fontWeight="medium">{role.code}</Text>
                                <Text
                                  fontSize="xs"
                                  color={
                                    field.value === role.code
                                      ? "whiteAlpha.800"
                                      : mutedText
                                  }
                                >
                                  Poziom: {role.level}
                                </Text>
                              </Box>
                              {field.value === role.code && <ViewIcon />}
                            </Flex>
                          </Box>
                        ))}
                      </Stack>
                    )
                  }}
                />
              )}
              {errors.roleCode && (
                <FormErrorMessage>
                  {errors.roleCode.message as string}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <Checkbox {...register("isActive")} colorScheme="green">
                Aktywne konto?
              </Checkbox>
            </FormControl>

            <HStack justify="space-between" flexWrap="wrap" spacing={3}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => resetPasswordMutation.mutate(user.email)}
                isLoading={resetPasswordMutation.isPending}
                isDisabled={!user.email}
              >
                Wyślij email resetujący
              </Button>
              <Button
                size="sm"
                type="submit"
                colorScheme="green"
                isLoading={isSubmitting || updateMutation.isPending}
                isDisabled={!isDirty}
              >
                Zapisz zmiany
              </Button>
            </HStack>
          </Stack>
        </Box>
      </Stack>
    </Container>
  )
}
